import React, { useMemo, useState } from "react";
import { View, Text, StyleSheet, Platform, Alert } from "react-native";
import { useSelector, useDispatch } from "react-redux";
import { collection, getFirestore, addDoc, doc } from "firebase/firestore";
import { firebaseApp } from "../../firebase/config";
import WizardForm from "../../components/WizardForm";
import { getPPEFormQuestions } from "../../utils/helpers/equipmentForms";
import SectionCard from "../../components/SectionCard";
import EmptyState from "../../components/EmptyState";
import { executeFirestoreWriteWithQueue } from "../../utils/offlineWriteQueue";
import { buildingListActions } from "../../store/building-slice";

export default function PPEFormScreen({ route, navigation }) {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const date = useMemo(() => new Date(), []);
  const dateString = useMemo(
    () => `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`,
    [date]
  );

  const userUid = useSelector((state) => state.auth.uid);
  const { siteId } = route.params;
  const db = getFirestore(firebaseApp);
  const selectedBuilding = useSelector((state) => state.buildingList.selectedBuilding);
  const siteIsAdhoc = selectedBuilding?.isAdhoc || false;
  const siteName = selectedBuilding?.name || selectedBuilding?.site_name || siteId;

  const questions = useMemo(() => getPPEFormQuestions(), []);

  const siteRef = doc(db, siteIsAdhoc ? "adhocSites" : "BuildingsX", siteId);
  const formsRef = collection(siteRef, "windowCleaningForms");

  const submitForm = async (questionsToSave) => {
    setFormError("");
    setIsSubmitting(true);

    try {
      const cleanedQuestions = questionsToSave.map((q) => ({
        question: q.question,
        value: q.value,
        comments: q.comments || "",
      }));

      const docToSave = {
        response: cleanedQuestions,
        completedBy: userUid,
        date,
        type: "PPE",
      };

      const { queued } = await executeFirestoreWriteWithQueue({
        db,
        queueLabel: "ppe-form",
        writeOperation: async () => {
          await addDoc(formsRef, docToSave);
        },
      });

      dispatch(buildingListActions.setPpeForm(docToSave));

      const successMessage = queued
        ? "Offline: PPE checklist saved locally and queued for sync."
        : "Thanks — your PPE checklist has been saved.";
      if (Platform.OS === "web") {
        alert(successMessage);
        navigation.navigate("Building");
      } else {
        Alert.alert("Saved", successMessage, [
          { text: "OK", onPress: () => navigation.navigate("Building") },
        ]);
      }
    } catch (error) {
      const message = error?.message || "Unable to save the form. Please try again.";
      setFormError(message);
      if (Platform.OS !== "web") {
        Alert.alert("Error", message);
      }
      console.log(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!selectedBuilding) {
    return (
      <EmptyState
        title="No site selected"
        message="Choose a building from the dashboard before completing PPE checks."
        actionLabel="Go back"
        onAction={() => navigation.navigate("Home")}
      />
    );
  }

  return (
    <View style={styles.page}>
      <View style={styles.pageContent}>
        <Text style={styles.pageTitle}>PPE Inspection</Text>
        <Text style={styles.pageSubtitle}>
          Complete the PPE checklist to confirm all safety equipment is in place.
        </Text>

        <SectionCard
          title="Site information"
          subtitle="Confirm the location and date before completing PPE checks."
        >
          <View style={styles.siteInfoRow}>
            <View style={styles.siteInfoBlock}>
              <Text style={styles.siteInfoLabel}>Site</Text>
              <Text style={styles.siteInfoValue}>{siteName}</Text>
            </View>
            <View style={styles.siteInfoBlock}>
              <Text style={styles.siteInfoLabel}>Date</Text>
              <Text style={styles.siteInfoValue}>{dateString}</Text>
            </View>
          </View>
        </SectionCard>

        {!!formError && <Text style={styles.formError}>{formError}</Text>}

        <SectionCard
          title="PPE checklist"
          subtitle="Tap the responses and complete each item clearly."
        >
          <WizardForm
            questions={questions}
            submitForm={submitForm}
            isSubmitting={isSubmitting}
          />
        </SectionCard>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: "#f8fbff",
  },
  pageContent: {
    width: "100%",
    maxWidth: 780,
    alignSelf: "center",
    paddingTop: 22,
    paddingBottom: 32,
    paddingHorizontal: 16,
  },
  pageTitle: {
    fontSize: 30,
    fontWeight: "800",
    color: "#111827",
    marginBottom: 8,
  },
  pageSubtitle: {
    fontSize: 15,
    color: "#4b5563",
    lineHeight: 22,
    marginBottom: 18,
  },
  siteInfoRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  siteInfoBlock: {
    flex: 1,
    minWidth: 140,
    backgroundColor: "#eef2ff",
    borderRadius: 16,
    padding: 14,
    marginRight: 12,
    marginBottom: 12,
  },
  siteInfoLabel: {
    fontSize: 12,
    color: "#475569",
    textTransform: "uppercase",
    marginBottom: 6,
    letterSpacing: 0.5,
  },
  siteInfoValue: {
    fontSize: 16,
    fontWeight: "700",
    color: "#0f172a",
  },
  formError: {
    marginBottom: 16,
    padding: 14,
    borderRadius: 18,
    backgroundColor: "#fff1f2",
    borderWidth: 1,
    borderColor: "#fecaca",
    color: "#b91c1c",
    fontSize: 14,
    lineHeight: 20,
  },
});
